import { useEffect, useState } from 'react'

import { SearchField } from '@forwardflow/ui/SearchField'
import { Button } from '@forwardflow/ui'

import {
   InviteAgentModal,
   RemovePhoneNumberModal,
   ReplacePhoneNumberModal,
} from '@forwardflow/modals'

import { UserListItem } from '../UserListItem'
import { useOrganizationUsers } from '@forwardflow/hooks/customHooks/organization'
import { useOrganizationUsersFilter } from '@forwardflow/store/filter'
import { useInviteAgentModalStore } from '@forwardflow/store/modal'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { message } from 'antd'

// Helper function to initialize user permissions from isWhiteListed field
const initializeUserPermissions = (users: any[]): Record<string, 'yes' | 'no' | null> => {
   const permissions: Record<string, 'yes' | 'no' | null> = {}
   users.forEach((user: any) => {
      if (user.id != null) {
         if (typeof user.isWhiteListed === 'boolean') {
            permissions[String(user.id)] = user.isWhiteListed ? 'yes' : 'no'
         } else {
            permissions[String(user.id)] = null
         }
      }
   })
   return permissions
}

const UserManagement = () => {
   const { openInviteAgentModal, isInviteAgentModalOpen } =
      useInviteAgentModalStore()
   const { page, limit } = useOrganizationUsersFilter()
   const { data: pendingUsers, mutate: refetchPendingUsers } =
      useOrganizationUsers({
         page: page,
         limit: limit,
         invitationStatus: 'pending',
      })

   const { data: acceptedUsers, mutate: refetchAcceptedUsers } =
      useOrganizationUsers({
         page: page,
         limit: limit,
         invitationStatus: 'accepted',
      })

   const [allUsers, setAllUsers] = useState<any[]>([])
   const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<number[]>(
      []
   )
   const [searchQuery, setSearchQuery] = useState('')
   const [isEditingWhitelist, setIsEditingWhitelist] = useState(false)

   const filteredUsers = allUsers.filter(
      (user) =>
         user.firstName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
         user.email?.toLowerCase().includes(searchQuery?.toLowerCase())
   )

   const filteredAcceptedUsers = filteredUsers.filter(
      (user) => user.invitationStatus === 'accepted'
   )
   const filteredPendingUsers = filteredUsers.filter(
      (user) => user.invitationStatus === 'pending'
   )

   const whitelistedCount = allUsers.filter(
      (user) => user.isWhiteListed === true
   ).length

   const [userPermissions, setUserPermissions] = useState<
      Record<string, 'yes' | 'no' | null>
   >({})

   useEffect(() => {
      if (!isInviteAgentModalOpen) {
         refetchAcceptedUsers?.()
         refetchPendingUsers?.()
      }
   }, [isInviteAgentModalOpen])

   useEffect(() => {
      const accepted = acceptedUsers?.data || []
      const pending = pendingUsers?.data || []
      const combined = [...accepted, ...pending]
      const unique = combined.filter(
         (user, index, self) =>
            index ===
            self.findIndex(
               (u) =>
                  u.email === user.email &&
                  (u.invitationStatus === 'accepted' ||
                     u.invitationStatus === 'pending')
            )
      )
      setAllUsers(unique)
      setUserPermissions(initializeUserPermissions(unique))
   }, [acceptedUsers, pendingUsers])

   const handleSelectionChange = (id: number, isSelected: boolean) => {
      setSelectedPhoneNumbers((prev) =>
         isSelected ? [...prev, id] : prev.filter((num) => num !== id)
      )
   }
   const handlePermissionChange = (
      userId: string,
      permission: 'yes' | 'no' | null
   ) => {
      setUserPermissions((prev) => ({
         ...prev,
         [userId]: permission,
      }))
   }

   const handleSubmitAllUsers = async () => {
      const whitelistedEmails = Object.entries(userPermissions)
         .filter(([, permission]) => permission === 'yes')
         .map(([userId]) => {
            const user = allUsers.find((u) => u.id.toString() === userId)
            return user?.email
         })
         .filter((email) => email)

      const nonWhitelistedEmails = Object.entries(userPermissions)
         .filter(([, permission]) => permission === 'no')
         .map(([userId]) => {
            const user = allUsers.find((u) => u.id.toString() === userId)
            return user?.email
         })
         .filter((email) => email)

      // Prepare API payloads
      const whitelistPayload = {
         emails: whitelistedEmails,
         isWhiteListed: true,
      }

      const nonWhitelistPayload = {
         emails: nonWhitelistedEmails,
         isWhiteListed: false,
      }

      try {
         if (whitelistedEmails.length > 0) {
            await OrganizationService.patchWhitelistAPI(whitelistPayload)
         }
         if (nonWhitelistedEmails.length > 0) {
            await OrganizationService.patchWhitelistAPI(nonWhitelistPayload)
         }
         message.success('Whitelist updated successfully')
      } catch (error) {
         console.error('Error updating whitelist:', error)
         message.error('Failed to update whitelist')
      } finally {
         refetchAcceptedUsers?.()
         refetchPendingUsers?.()
      }
   }

   const handleEditWhitelist = () => {
      setIsEditingWhitelist(true)
   }

   const handleCancelEdit = () => {
      setIsEditingWhitelist(false)
      setUserPermissions(initializeUserPermissions(allUsers))
   }

   const handleDoneEdit = () => {
      handleSubmitAllUsers()
      setIsEditingWhitelist(false)
   }

   // Render user lists component
   const renderUserLists = () => (
      <>
         <div className="flex items-center mr-4 bg-primary/10">
            <SearchField
               placeholder="Search Users"
               className="!rounded-lg !text-[14px] !w-full !max-w-full !bg-transparent"
               value={searchQuery}
               onChange={(value) => setSearchQuery(value)}
            />
         </div>

         <div className="flex text-[12px] w-full items-center justify-between px-10 pt-5 pb-3 text-white/70">
            <span>USERS</span>
            {!isEditingWhitelist && whitelistedCount > 0 && (
               <span>{whitelistedCount} Whitelisted Agents</span>
            )}
         </div>
         <div className="flex flex-col gap-y-1">
            {filteredAcceptedUsers?.map((item, index) => (
               <UserListItem
                  email={item.email}
                  isAdded={false}
                  hideActionButton={isEditingWhitelist}
                  key={index}
                  userId={item.id}
                  name={item.firstName + ' ' + item.lastName}
                  role={item.roles[0] || ''}
                  onSelectionChange={(isSelected) =>
                     handleSelectionChange(item.id, isSelected)
                  }
                  isSelected={selectedPhoneNumbers.includes(item.id)}
                  onPermissionChange={handlePermissionChange}
                  defaultPermission={userPermissions[item.id]}
                  isWhitelist={item.isWhiteListed}
                  showPermissionButtons={isEditingWhitelist}
               />
            ))}
         </div>

         <div className="flex text-[12px] px-10 pt-5 pb-3">
            <span className="text-white/70">PENDING USERS</span>
         </div>
         <div className="flex flex-col gap-y-1">
            {filteredPendingUsers?.map((item, index) => (
               <UserListItem
                  isPending={!isEditingWhitelist}
                  email={item.email}
                  isAdded={false}
                  hideActionButton={isEditingWhitelist}
                  key={index}
                  userId={item.id}
                  name={item.firstName + ' ' + item.lastName}
                  role={item.roles[0] || ''}
                  onSelectionChange={(isSelected) =>
                     handleSelectionChange(item.id, isSelected)
                  }
                  isSelected={selectedPhoneNumbers.includes(item.id)}
                  onPermissionChange={handlePermissionChange}
                  defaultPermission={userPermissions[item.id]}
                  isWhitelist={item.isWhiteListed}
                  showPermissionButtons={isEditingWhitelist}
               />
            ))}
         </div>
      </>
   )

   return (
      <div className="flex flex-col flex-1">
         {!isEditingWhitelist ? (
            <>
               <div className="flex items-center justify-between py-6 pl-4 pr-4">
                  <span className="mr-4 font-medium">User management</span>
                  <div className="flex items-center justify-between gap-4">
                     <Button
                        onClick={handleEditWhitelist}
                        className="!h-10 !rounded bg-white/10 hover:bg-white/15 !border-white/10 px-7 !mt-0 !w-40 whitespace-nowrap"
                     >
                        Edit Whitelist
                     </Button>
                     <Button
                        onClick={openInviteAgentModal}
                        className="!h-10 !rounded bg-primary/10 hover:bg-primary/15 border-primary px-7 !mt-0 max-w-40"
                     >
                        Add User
                     </Button>
                  </div>
               </div>
               {renderUserLists()}
            </>
         ) : (
            <>
               <div className="flex flex-col pt-6 pl-4 mb-4 ">
                  <p className="mb-6 mr-4 font-medium">User management</p>
                  <div className="flex items-center justify-between gap-4 border-y border-black/20">
                     <p>Edit Agent Whitelist</p>
                     <div className="flex items-center gap-3 px-4 py-3">
                        <Button
                           className="!h-10 !rounded bg-white/10 hover:bg-white/15 !border-white/10 px-7 !mt-0 !w-28"
                           onClick={handleCancelEdit}
                        >
                           Cancel
                        </Button>
                        <Button
                           className="!rounded !w-28 !mt-0 !h-10"
                           onClick={handleDoneEdit}
                        >
                           Done
                        </Button>
                     </div>
                  </div>
               </div>
               {renderUserLists()}
            </>
         )}

         <InviteAgentModal />
         <ReplacePhoneNumberModal />
         <RemovePhoneNumberModal />
      </div>
   )
}
export default UserManagement
